import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Tooltip } from "antd";
import copy from "copy-to-clipboard";
import CustomIcon from "../../../../Components/CustomIcon";
import { useToast } from "../../../../Hooks/useToast";
import FieldStatusIndicator, { useFieldIndicator } from "../../../../Components/FieldStatusIndicator";
import { toOperationalStatusIndicatorRecord } from "../utils/tableUtils";

const OPERATIONAL_STATUS_INDICATOR = "operationalStatus";

// ─── SOH colour helper ─────────────────────────────────────────────────────────
export function getSohColor(soh) {
  if (soh > 98)  return "#1B7A42";
  if (soh >= 95) return "#4CAF72";
  if (soh >= 90) return "#7EC862";
  if (soh >= 85) return "#B4D95A";
  if (soh >= 80) return "#F5C430";
  if (soh >= 75) return "#E87040";
  return "#C62828";
}

// ─── Format days as "X yrs, Y mo" (mirrors StateOfHealthChart.jsx) ────────────
export function formatDaysAge(days) {
  if (days == null || isNaN(days)) return "—";
  const d = Math.round(days);
  if (d < 30) return `${d} day${d !== 1 ? "s" : ""}`;
  const years  = Math.floor(d / 365);
  const months = Math.floor((d % 365) / 30);
  if (years === 0) return `${months} month${months !== 1 ? "s" : ""}`;
  if (months === 0) return `${years} year${years !== 1 ? "s" : ""}`;
  return `${years} yr${years !== 1 ? "s" : ""}, ${months} mo`;
}

// ─── License plate cell with navigation ───────────────────────────────────────
// showStatusDot=false hides the online/offline dot, for sources without pingStatus.
// operationalStatusIndicator=true hangs the shared "operationalStatus" field marker off
// the text (only when the org has fieldIndicators.enabled), reading the row's
// operationalStatus. Off by default, so existing drill-downs are unchanged.
const LicensePlateCell = ({
  record,
  onNavigate,
  text: textProp,
  showStatusDot = true,
  operationalStatusIndicator = false,
}) => {
  const { openNotification } = useToast();
  const isActive = record?.pingStatus?.toLowerCase() === "active";
  const text = textProp ?? record?.deviceID;
  const indicatorName = operationalStatusIndicator ? OPERATIONAL_STATUS_INDICATOR : undefined;
  const indicatorRecord = operationalStatusIndicator ? toOperationalStatusIndicatorRecord(record) : undefined;
  // Whether the cell carries a marker track at all (same for every row): as in
  // DeviceIDLink, the text block then grows so the copy icon stays put.
  const { active: hasIndicator } = useFieldIndicator({ name: indicatorName, record: indicatorRecord });

  const handleCopy = (e) => {
    e.stopPropagation();
    copy(text);
    openNotification({ text: "Copied to Clipboard" });
  };

  const label = (
    <Typography
      variant="fw400s14lh24c0061CA"
      onClick={onNavigate}
      sx={{ cursor: "pointer", "&:hover": { color: "#4D44D8 !important" } }}
    >
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ...(hasIndicator ? { width: "100%" } : {}) }}>
      {showStatusDot && (
        <Box
          data-testid="license-plate-status-dot"
          sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: isActive ? "#2DB55D" : "#9E9E9E" }}
        />
      )}
      {hasIndicator ? (
        <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flexGrow: 1 }}>
          <FieldStatusIndicator name={indicatorName} record={indicatorRecord}>
            {label}
          </FieldStatusIndicator>
        </Box>
      ) : label}
      <Tooltip title="Click to copy" placement="top">
        <IconButton disableRipple disableTouchRipple disableFocusRipple onClick={handleCopy}>
          <CustomIcon name="copy-icon" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// ─── Empty-cell text: column defaultValue from org meta, else the renderer's own ─
// Batteries in the "SOH Unavailable" / "RUL Unavailable" buckets have null metrics.
export function getEmptyCellText(config, fallback) {
  return config?.defaultValue ?? fallback;
}

// ─── String label: stringDisplayID, falling back to stringNo ───────────────────
export function getStringLabel(record) {
  const display = record?.stringDisplayID;
  if (display != null && display !== "") return String(display);
  const no = record?.stringNo;
  if (no != null && no !== "") return String(no);
  return null;
}

// ─── Custom renderers factory ─────────────────────────────────────────────────
export const getSohBreakdownCustomRenderers = (
  onNavigate,
  { showStatusDot = true, operationalStatusIndicator = false } = {}
) => ({
  "vehicle-license-plate": (record, config) => (
    <LicensePlateCell
      record={record}
      onNavigate={() => onNavigate(record)}
      text={config?.dataIndex ? record?.[config.dataIndex] : undefined}
      showStatusDot={showStatusDot}
      operationalStatusIndicator={operationalStatusIndicator}
    />
  ),

  "digital-twin-link": (record, config) => (
    <LicensePlateCell
      record={record}
      onNavigate={() => onNavigate(record)}
      text={config?.dataIndex ? record?.[config.dataIndex] : undefined}
      showStatusDot={showStatusDot}
      operationalStatusIndicator={operationalStatusIndicator}
    />
  ),

  // String-level SOH history rows: the string's display id, else its number.
  "soh-string-label": (record, column) => (
    <Typography sx={{ fontSize: 13, color: "#202124" }}>
      {getStringLabel(record) ?? getEmptyCellText(column, "-")}
    </Typography>
  ),

  "days-to-duration": (record, column) => {
    const days = record?.[column.dataIndex];
    const text = days == null || isNaN(days) ? getEmptyCellText(column, formatDaysAge(days)) : formatDaysAge(days);
    return <Typography sx={{ fontSize: 13, color: "#202124" }}>{text}</Typography>;
  },

  "soh-progress-bar": (record, config) => {
    const soh = record?.sohEst;
    if (soh == null) return <Typography sx={{ fontSize: 13, color: "#9E9E9E" }}>{getEmptyCellText(config, "N/A")}</Typography>;
    const decimalPlaces = config?.decimalPlaces ?? 1;
    const color = getSohColor(soh);
    const width = Math.min(100, Math.max(0, soh));
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 56, height: 5, bgcolor: "#E0E0E0", borderRadius: "3px", flexShrink: 0 }}>
          <Box
            sx={{
              width: `${width}%`,
              height: "100%",
              bgcolor: color,
              borderRadius: "3px",
            }}
          />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#202124" }}>{soh.toFixed(decimalPlaces)}%</Typography>
      </Box>
    );
  },
});

// ─── Column definitions (keys match API response field names) ─────────────────
export const SOH_BREAKDOWN_COLUMN_ORDER = [
  "deviceID",
  "batteryID",
  "sohEst",
  "calendarAgeDaysEst",
  "eqCycleEst",
  "odometer",
  "latestSOHPredictionOdometer",
  "RULCalendarAgeDaysPredicted",
  "RULCyclesPredicted",
  "RULKmPredicted",
];

export const SOH_BREAKDOWN_COLUMNS_CONFIG = {
  deviceID: {
    title: "Bus Number",
    headerLabel: "Bus Number",
    dataIndex: "deviceID",
    key: "deviceID",
    valueRenderingType: "vehicle-license-plate",
    width: 220,
    fixed: "left",
    sorter: false,
  },
  batteryID: {
    title: "Battery ID",
    headerLabel: "Battery ID",
    dataIndex: "batteryID",
    key: "batteryID",
    valueRenderingType: "text",
    width: 200,
    sorter: false,
  },
  sohEst: {
    title: "SOH (%)",
    headerLabel: "SOH (%)",
    dataIndex: "sohEst",
    key: "sohEst",
    valueRenderingType: "soh-progress-bar",
    width: 180,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  calendarAgeDaysEst: {
    title: "Age",
    headerLabel: "Age",
    dataIndex: "calendarAgeDaysEst",
    key: "calendarAgeDaysEst",
    valueRenderingType: "days-to-duration",
    width: 200,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  eqCycleEst: {
    title: "Cycles Completed",
    headerLabel: "Cycles Completed",
    dataIndex: "eqCycleEst",
    key: "eqCycleEst",
    valueRenderingType: "number",
    width: 200,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  odometer: {
    title: "km Covered",
    headerLabel: "km Covered",
    dataIndex: "odometer",
    key: "odometer",
    valueRenderingType: "number",
    width: 160,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  // Odometer captured at the time the SOH value was last computed (distinct from
  // the vehicle's current `odometer`). Nullable until the data pipeline backfills it.
  latestSOHPredictionOdometer: {
    title: "Odometer",
    headerLabel: "Odometer",
    dataIndex: "latestSOHPredictionOdometer",
    key: "latestSOHPredictionOdometer",
    valueRenderingType: "distance-km",
    defaultValue: "-",
    width: 220,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  RULCalendarAgeDaysPredicted: {
    title: "RUL (Months & Years)",
    headerLabel: "RUL (Months & Years)",
    dataIndex: "RULCalendarAgeDaysPredicted",
    key: "RULCalendarAgeDaysPredicted",
    valueRenderingType: "days-to-duration",
    width: 210,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  RULCyclesPredicted: {
    title: "RUL (Cycles)",
    headerLabel: "RUL (Cycles)",
    dataIndex: "RULCyclesPredicted",
    key: "RULCyclesPredicted",
    valueRenderingType: "number",
    width: 160,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
  RULKmPredicted: {
    title: "RUL (km)",
    headerLabel: "RUL (km)",
    dataIndex: "RULKmPredicted",
    key: "RULKmPredicted",
    valueRenderingType: "number",
    width: 150,
    sortDirections: ["ascend", "descend", "ascend"],
    sorter: true,
  },
};

